<template>
  <div class="field">
    <div class="field is-horizontal" v-if="showType">
      <div class="field-label">
        <label class="label">
          Type
          <n-tooltip position="top" text="The type of characters that will be accepted as valid input" />
        </label>
      </div>
      <div class="field-body">
        <div class="control">
          <div class="select is-fullwidth">
            <select v-model="selectedType" @change="patternChanged">
              <option
                v-for="(patternValue, patternLabel) in AVAILABLE_PATTERN_TYPES"
                :key="patternLabel"
                :value="patternLabel"
              >
                {{ patternLabel }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <div class="field is-horizontal">
      <div class="field-label">
        <label class="label">
          Min / Max Length
          <n-tooltip
            position="top"
            text="Minimum and Maximum allowed character limits. If left blank, there is no limit"
          />
        </label>
      </div>
      <div class="field-body">
        <div class="field">
          <div class="control">
            <input class="input" type="text" v-model.trim="$v.minLength.$model" @change="patternChanged" />
            <p class="help is-danger" v-if="$v.minLength.$error && !$v.minLength.numeric">
              Min length must be a number
            </p>
          </div>
        </div>
        <div class="field">
          <div class="control">
            <input class="input" type="text" v-model.trim="$v.maxLength.$model" @change="patternChanged" />
            <p class="help is-danger" v-if="$v.maxLength.$error && !$v.maxLength.numeric">
              Max length must be a number
            </p>
            <p class="help is-danger" v-if="$v.maxLength.$error && !$v.maxLength.greaterThan">
              Max length must be greater than the Min length
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { numeric, minValue } from '@vuelidate/validators';
import { ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';

const AVAILABLE_PATTERN_TYPES: { [key: string]: string } = {
  Any: '.',
  Alphanumeric: '[a-zA-Z0-9]',
  'Letters Only': '[a-zA-Z]',
  'Numbers Only': '[0-9]',
  Decimal: '(\\d+(\\.\\d{0,2})?|\\.?\\d{1,2})',
};

const greaterThan = (value: string, vm: any) => {
  if (vm.maxLength.value === '' || vm.minLength.value === '') {
    return true; // No value for either min or max, its valid
  }
  return parseInt(value, 10) >= parseInt(vm.minLength.value, 10);
};

const props = defineProps({
  value: {
    type: String,
    default: '.',
  },
  showType: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  (e: 'updated', pattern: string, invalid: boolean): void;
}>();

const selectedType = ref('');
const minLength = ref('');
const maxLength = ref('');

const validations = {
  minLength: {
    numeric,
    minValue: minValue(0),
  },
  maxLength: {
    numeric,
    minValue: minValue(0),
    greaterThan,
  },
};

const $v = useVuelidate(validations, { minLength, maxLength });

watch(
  () => props.value,
  () => {
    parsePattern();

    $v.value.$touch();
    patternChanged();
  },
  { immediate: true },
);

function parsePattern() {
  if (!props.value || props.value.length === 0) {
    selectedType.value = 'Any';
    return;
  }
  selectedType.value = getSelectedType() || 'Any';

  if (canBeAnyLength()) {
    minLength.value = '';
    maxLength.value = '';
  } else if (props.value.indexOf('{') > -1) {
    const openingBrace = props.value.indexOf('{') + 1;
    const closingBrace = props.value.indexOf('}');
    const lengths = props.value.substring(openingBrace, closingBrace).split(',');

    minLength.value = lengths[0];
    maxLength.value = lengths[1];
  }
}

function buildPattern() {
  let pattern: string = `${AVAILABLE_PATTERN_TYPES[selectedType.value]}`;
  if (selectedType.value === 'Decimal') {
    pattern = buildDecimalPattern(pattern);
  } else {
    if (minLength.value.length === 0 && maxLength.value.length === 0) {
      pattern += '+';
    } else {
      if (minLength.value.length === 0) {
        pattern += `{1,${maxLength.value}}`;
      } else {
        pattern += `{${minLength.value},${maxLength.value}}`;
      }
    }
  }
  pattern = `^${pattern}$`;

  return pattern;
}

function patternChanged() {
  const newPattern = buildPattern();
  emit('updated', newPattern, $v.value.$invalid);
}

function getSelectedType() {
  const keys = Object.keys(AVAILABLE_PATTERN_TYPES);
  keys.shift();
  return keys.find((key) => props.value.indexOf(AVAILABLE_PATTERN_TYPES[key]) > -1);
}

function canBeAnyLength() {
  if (isRegexEndedWithPlusSymbol()) {
    return true;
  } else if (selectedType.value === 'Decimal') {
    return canDecimalBeAnyLength();
  }
  return false;
}

function isRegexEndedWithPlusSymbol() {
  const regExWithoutStartEndLineCharacters = stripLineStartAndEndCharacters(props.value);
  return regExWithoutStartEndLineCharacters.endsWith('+');
}

function canDecimalBeAnyLength() {
  const regExWithoutStartEndLineCharacters = stripLineStartAndEndCharacters(props.value);
  return isRegexDefaultDecimalRegex(regExWithoutStartEndLineCharacters);
}

function isRegexDefaultDecimalRegex(regex: string) {
  return AVAILABLE_PATTERN_TYPES.Decimal === regex;
}

function stripLineStartAndEndCharacters(input: string) {
  if (input.startsWith('^') && input.endsWith('$')) {
    return input.substring(1, input.length - 1);
  }
  return input;
}

function buildDecimalPattern(pattern: string) {
  if (minLength.value.length || maxLength.value.length) {
    if (minLength.value.length === 0) {
      pattern = `(?=^.{1,${maxLength.value}}$)${pattern}`;
    } else {
      pattern = `(?=^.{${minLength.value},${maxLength.value}}$)${pattern}`;
    }
  }
  return pattern;
}
</script>
